import { IconNames } from '@blueprintjs/icons';
import { Button } from '@strata/react-buttons';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type ColumnDefGeneric,
    GridWithSearch,
    PrimaryData,
    SecondaryData,
} from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { PageTitle } from '@strata/react-page-title';
import { Icon } from '@strata/react-status-icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { DeleteUserAccountDialog } from 'components/admin/userAccounts/dialogs/DeleteUserAccountDialog';
import { formatUserNameLastFirst } from 'components/admin/userAccounts/userAccountHelpers';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { type OrganizationUser, useGetOrganizationUsersQuery } from 'store/api/authApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';

type UserAccountRow = OrganizationUser & { formattedName: string; actions?: never };

export const UserAccountsListPage = () => {
    const navigate = useNavigate();
    const userCanEdit = usePermissionCheck(Permissions.WRITE_SUPPLIER_USERS);
    const loggedInSupplierName = useLoggedInSupplierName();
    const loggedInUserUpn = useAppSelector((state) => state.auth.profile?.email);

    const { data: organizationUsers, isFetching } = useGetOrganizationUsersQuery();
    const userAccountRows = useMemo(
        () =>
            organizationUsers?.map((user) => ({
                ...user,
                formattedName: formatUserNameLastFirst(user.full_name),
            })),
        [organizationUsers],
    );

    const [userToDelete, setUserToDelete] = useState<OrganizationUser>();

    const columns = useMemo<ColumnDef<UserAccountRow>[]>(
        () => [
            {
                accessorKey: 'formattedName',
                header: 'User',
                cell: ({ getValue }) => <PrimaryData data={getValue()} />,
            },
            {
                accessorKey: 'upn',
                header: 'Email',
                cell: ({ getValue }) => <SecondaryData data={getValue()} />,
            },
            {
                accessorKey: 'display_name',
                header: 'Role',
            },
            ...(userCanEdit
                ? [
                      {
                          accessorKey: 'actions',
                          header: 'Actions',
                          enableSorting: false,
                          size: 70,
                          cell: ({ row }) => {
                              const isLoggedInUser =
                                  loggedInUserUpn?.toLowerCase() === row.original.upn.toLowerCase();

                              return (
                                  <div className="sds_grid-actions-row">
                                      <Icon
                                          icon={IconNames.EDIT}
                                          intent={Icon.Intent.SECONDARY}
                                          title="Edit"
                                          onClick={() =>
                                              navigate(AppRoutes.userAccountEdit(row.original.upn))
                                          }
                                      />
                                      {loggedInUserUpn && !isLoggedInUser && (
                                          <Icon
                                              icon={IconNames.TRASH}
                                              intent={Icon.Intent.SECONDARY}
                                              title="Delete"
                                              onClick={() => setUserToDelete(row.original)}
                                          />
                                      )}
                                  </div>
                              );
                          },
                      } as ColumnDefGeneric<UserAccountRow, 'actions'>,
                  ]
                : []),
        ],
        [loggedInUserUpn, navigate, userCanEdit],
    );

    return (
        <FormPageContainer>
            <div
                className="flex__gap--medium padding__around--medium"
                style={{ flexDirection: 'column', height: '100%' }}
            >
                <PageTitle title="User Accounts" secondaryTitle={loggedInSupplierName} />
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    wrapText
                    label="For assistance with account creation, user setup, or access issues, contact access@freemarkethealth.com."
                />
                <DeleteUserAccountDialog
                    userToDelete={userToDelete}
                    onClose={() => setUserToDelete(undefined)}
                />
                <div style={{ overflow: 'auto', flexGrow: 1 }}>
                    <GridWithSearch<UserAccountRow>
                        data={userAccountRows}
                        isLoading={isFetching}
                        sortProperties={[{ id: 'formattedName', desc: false }]}
                        rightHeaderContent={
                            userCanEdit ? (
                                <Button
                                    label="Add User"
                                    id="add-user-account-button"
                                    intent={Button.Intent.PRIMARY}
                                    onClick={() => navigate(AppRoutes.userAccountCreate)}
                                />
                            ) : undefined
                        }
                        columns={columns}
                    />
                </div>
            </div>
        </FormPageContainer>
    );
};
